import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { TrendingUp, Info, ChevronDown, ChevronUp, Target, Ruler, Scale } from 'lucide-react';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

import { API } from '../config/api';

// UK-WHO Centile Reference Data (Boys, simplified for key centiles)
// These are approximations for visualization purposes
const WHO_CENTILES_BOYS = {
  height: {
    // Age in years -> height in cm for each centile
    3: { 5: 103, 6: 109, 7: 115, 8: 120, 9: 125, 10: 130, 11: 135, 12: 141, 13: 148, 14: 156, 15: 163, 16: 168, 17: 171, 18: 172 },
    15: { 5: 107, 6: 113, 7: 119, 8: 125, 9: 130, 10: 136, 11: 141, 12: 148, 13: 155, 14: 163, 15: 170, 16: 175, 17: 177, 18: 178 },
    50: { 5: 110, 6: 117, 7: 123, 8: 129, 9: 135, 10: 141, 11: 147, 12: 154, 13: 162, 14: 170, 15: 176, 16: 180, 17: 182, 18: 183 },
    85: { 5: 114, 6: 121, 7: 128, 8: 134, 9: 140, 10: 147, 11: 153, 12: 161, 13: 169, 14: 177, 15: 183, 16: 186, 17: 188, 18: 189 },
    97: { 5: 117, 6: 125, 7: 132, 8: 138, 9: 145, 10: 152, 11: 159, 12: 167, 13: 176, 14: 183, 15: 189, 16: 191, 17: 193, 18: 194 }
  },
  weight: {
    3: { 5: 15, 6: 17, 7: 19, 8: 21, 9: 23, 10: 26, 11: 29, 12: 32, 13: 36, 14: 41, 15: 46, 16: 50, 17: 53, 18: 55 },
    15: { 5: 17, 6: 19, 7: 22, 8: 24, 9: 27, 10: 31, 11: 35, 12: 39, 13: 44, 14: 50, 15: 55, 16: 59, 17: 62, 18: 64 },
    50: { 5: 19, 6: 21, 7: 24, 8: 27, 9: 31, 10: 35, 11: 40, 12: 45, 13: 51, 14: 57, 15: 63, 16: 67, 17: 70, 18: 72 },
    85: { 5: 21, 6: 24, 7: 28, 8: 32, 9: 36, 10: 42, 11: 48, 12: 54, 13: 61, 14: 68, 15: 74, 16: 78, 17: 81, 18: 83 },
    97: { 5: 24, 6: 28, 7: 32, 8: 37, 9: 43, 10: 50, 11: 57, 12: 65, 13: 73, 14: 81, 15: 87, 16: 91, 17: 94, 18: 96 }
  },
  bmi: {
    3: { 5: 13.5, 6: 13.5, 7: 13.5, 8: 13.6, 9: 13.8, 10: 14.0, 11: 14.3, 12: 14.7, 13: 15.2, 14: 15.8, 15: 16.4, 16: 16.9, 17: 17.3, 18: 17.6 },
    15: { 5: 14.3, 6: 14.3, 7: 14.4, 8: 14.6, 9: 14.9, 10: 15.3, 11: 15.7, 12: 16.3, 13: 16.9, 14: 17.6, 15: 18.2, 16: 18.8, 17: 19.2, 18: 19.5 },
    50: { 5: 15.3, 6: 15.4, 7: 15.5, 8: 15.8, 9: 16.2, 10: 16.7, 11: 17.3, 12: 18.0, 13: 18.8, 14: 19.6, 15: 20.4, 16: 21.0, 17: 21.5, 18: 21.9 },
    85: { 5: 16.5, 6: 16.7, 7: 17.0, 8: 17.5, 9: 18.1, 10: 18.9, 11: 19.8, 12: 20.8, 13: 21.9, 14: 22.9, 15: 23.8, 16: 24.5, 17: 25.0, 18: 25.4 },
    97: { 5: 17.7, 6: 18.1, 7: 18.6, 8: 19.4, 9: 20.3, 10: 21.4, 11: 22.6, 12: 23.9, 13: 25.2, 14: 26.4, 15: 27.4, 16: 28.2, 17: 28.8, 18: 29.2 }
  }
};

// Girls data (similar structure)
const WHO_CENTILES_GIRLS = {
  height: {
    3: { 5: 102, 6: 108, 7: 114, 8: 119, 9: 124, 10: 130, 11: 136, 12: 143, 13: 149, 14: 153, 15: 155, 16: 156, 17: 157, 18: 157 },
    15: { 5: 106, 6: 112, 7: 118, 8: 124, 9: 130, 10: 136, 11: 143, 12: 150, 13: 156, 14: 159, 15: 161, 16: 162, 17: 163, 18: 163 },
    50: { 5: 110, 6: 116, 7: 122, 8: 128, 9: 135, 10: 141, 11: 149, 12: 156, 13: 161, 14: 164, 15: 166, 16: 167, 17: 167, 18: 168 },
    85: { 5: 114, 6: 121, 7: 127, 8: 134, 9: 140, 10: 148, 11: 155, 12: 162, 13: 167, 14: 170, 15: 172, 16: 173, 17: 173, 18: 173 },
    97: { 5: 118, 6: 125, 7: 132, 8: 138, 9: 146, 10: 153, 11: 161, 12: 167, 13: 172, 14: 175, 15: 177, 16: 178, 17: 178, 18: 178 }
  },
  weight: {
    3: { 5: 14, 6: 16, 7: 18, 8: 20, 9: 22, 10: 25, 11: 28, 12: 32, 13: 36, 14: 40, 15: 43, 16: 45, 17: 47, 18: 48 },
    15: { 5: 16, 6: 18, 7: 21, 8: 24, 9: 27, 10: 30, 11: 35, 12: 40, 13: 45, 14: 49, 15: 52, 16: 54, 17: 56, 18: 57 },
    50: { 5: 18, 6: 21, 7: 24, 8: 27, 9: 31, 10: 36, 11: 41, 12: 47, 13: 52, 14: 56, 15: 59, 16: 61, 17: 62, 18: 63 },
    85: { 5: 21, 6: 24, 7: 28, 8: 32, 9: 37, 10: 43, 11: 50, 12: 57, 13: 63, 14: 67, 15: 70, 16: 72, 17: 73, 18: 74 },
    97: { 5: 24, 6: 28, 7: 33, 8: 38, 9: 44, 10: 52, 11: 60, 12: 68, 13: 75, 14: 80, 15: 84, 16: 86, 17: 87, 18: 88 }
  },
  bmi: {
    3: { 5: 13.2, 6: 13.2, 7: 13.2, 8: 13.3, 9: 13.5, 10: 13.8, 11: 14.2, 12: 14.7, 13: 15.2, 14: 15.7, 15: 16.1, 16: 16.4, 17: 16.6, 18: 16.8 },
    15: { 5: 14.0, 6: 14.0, 7: 14.1, 8: 14.3, 9: 14.6, 10: 15.1, 11: 15.6, 12: 16.3, 13: 16.9, 14: 17.5, 15: 18.0, 16: 18.3, 17: 18.5, 18: 18.7 },
    50: { 5: 15.1, 6: 15.2, 7: 15.4, 8: 15.7, 9: 16.2, 10: 16.8, 11: 17.5, 12: 18.4, 13: 19.2, 14: 19.9, 15: 20.4, 16: 20.8, 17: 21.0, 18: 21.2 },
    85: { 5: 16.4, 6: 16.6, 7: 17.0, 8: 17.5, 9: 18.2, 10: 19.1, 11: 20.1, 12: 21.2, 13: 22.3, 14: 23.2, 15: 23.9, 16: 24.4, 17: 24.7, 18: 24.9 },
    97: { 5: 17.8, 6: 18.2, 7: 18.8, 8: 19.6, 9: 20.6, 10: 21.8, 11: 23.1, 12: 24.5, 13: 25.8, 14: 26.9, 15: 27.7, 16: 28.3, 17: 28.6, 18: 28.9 }
  }
};

const CENTILE_COLORS = {
  3: { line: 'rgba(255, 99, 132, 0.5)', label: '3rd' },
  15: { line: 'rgba(255, 159, 64, 0.5)', label: '15th' },
  50: { line: 'rgba(75, 192, 192, 0.6)', label: '50th' },
  85: { line: 'rgba(255, 159, 64, 0.5)', label: '85th' },
  97: { line: 'rgba(255, 99, 132, 0.5)', label: '97th' }
};

export default function UKWHOGrowthCharts({ childId, childName }) {
  const [loading, setLoading] = useState(true);
  const [measurements, setMeasurements] = useState([]);
  const [child, setChild] = useState(null);
  const [activeChart, setActiveChart] = useState('height');
  const [showInfo, setShowInfo] = useState(false);

  const loadData = React.useCallback(async () => {
    try {
      const response = await axios.get(`${API}/children/${childId}/growth-data`);
      setChild(response.data.child);
      setMeasurements(response.data.measurements || []);
    } catch (error) {
      console.error('Failed to load growth data:', error);
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <TrendingUp className="h-5 w-5 text-[#02b5ff]" />
          <h3 className="text-lg font-bold text-white">UK-WHO Growth Charts</h3>
        </div>
        <div className="text-center py-8 text-zinc-500 animate-pulse">Loading growth data...</div>
      </div>
    );
  }

  if (!measurements || measurements.length === 0) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <TrendingUp className="h-5 w-5 text-[#02b5ff]" />
          <h3 className="text-lg font-bold text-white">UK-WHO Growth Charts</h3>
        </div>
        <div className="text-center py-8">
          <Ruler className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">No measurements yet</p>
          <p className="text-zinc-500 text-sm">Add measurements to view growth centile charts</p>
        </div>
      </div>
    );
  }

  const gender = child?.gender?.toLowerCase() || 'male';
  const centileData = gender === 'female' ? WHO_CENTILES_GIRLS : WHO_CENTILES_BOYS;

  // Prepare child's measurement data
  const childPoints = measurements.map(m => ({
    age: m.age_years,
    height: m.standing_height,
    weight: m.weight,
    bmi: m.bmi,
    heightPercentile: m.uk_who_percentiles?.height_percentile,
    weightPercentile: m.uk_who_percentiles?.weight_percentile,
    bmiPercentile: m.bmi_percentile,
    date: new Date(m.measurement_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  })).sort((a, b) => a.age - b.age);

  // Get age range
  const minAge = Math.max(5, Math.floor(Math.min(...childPoints.map(p => p.age))));
  const maxAge = Math.min(18, Math.ceil(Math.max(...childPoints.map(p => p.age))) + 2);
  const ages = [];
  for (let age = minAge; age <= maxAge; age++) {
    ages.push(age);
  }

  // Build centile line datasets
  const centileDatasets = [3, 15, 50, 85, 97].map(centile => {
    const data = centileData[activeChart][centile];
    return {
      label: `${centile}th centile`,
      data: ages.map(age => data[age] || null),
      borderColor: CENTILE_COLORS[centile].line,
      backgroundColor: 'transparent',
      borderWidth: centile === 50 ? 2 : 1,
      borderDash: centile === 50 ? [] : [5, 5],
      pointRadius: 0,
      tension: 0.4,
      fill: false
    };
  });

  // Child's data points
  const childDataset = {
    label: `${child?.first_name || 'Child'}'s ${activeChart}`,
    data: ages.map(age => {
      const point = childPoints.find(p => Math.round(p.age) === age);
      return point ? point[activeChart] : null;
    }),
    borderColor: '#02b5ff',
    backgroundColor: '#02b5ff',
    borderWidth: 3,
    pointRadius: 8,
    pointBackgroundColor: '#02b5ff',
    pointBorderColor: '#fff',
    pointBorderWidth: 2,
    pointHoverRadius: 10,
    tension: 0.3,
    fill: false,
    spanGaps: true
  };

  // Projection line (if we have at least 2 measurements)
  let projectionDataset = null;
  if (childPoints.length >= 2) {
    const lastTwo = childPoints.slice(-2);
    const growthRate = (lastTwo[1][activeChart] - lastTwo[0][activeChart]) / (lastTwo[1].age - lastTwo[0].age);
    const lastPoint = childPoints[childPoints.length - 1];
    
    const projectionData = ages.map(age => {
      if (age < lastPoint.age) return null;
      if (age > lastPoint.age + 3) return null; // Project max 3 years
      return lastPoint[activeChart] + growthRate * (age - lastPoint.age);
    });

    projectionDataset = {
      label: 'Projected trend',
      data: projectionData,
      borderColor: '#ff025f',
      backgroundColor: 'transparent',
      borderWidth: 2,
      borderDash: [10, 5],
      pointRadius: 0,
      tension: 0.3,
      fill: false
    };
  }

  const chartData = {
    labels: ages.map(a => a.toString()),
    datasets: [
      ...centileDatasets,
      childDataset,
      ...(projectionDataset ? [projectionDataset] : [])
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        titleFont: { size: 13, weight: 'bold' },
        bodyFont: { size: 12 },
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          title: (items) => `Age: ${items[0].label} years`,
          label: (item) => {
            if (item.dataset.label.includes('centile')) {
              return `${item.dataset.label}: ${item.parsed.y?.toFixed(1)}`;
            }
            const point = childPoints.find(p => Math.round(p.age) === parseInt(item.label));
            if (point) {
              const percentile = activeChart === 'height' ? point.heightPercentile :
                                activeChart === 'weight' ? point.weightPercentile : point.bmiPercentile;
              return [
                `${activeChart}: ${item.parsed.y?.toFixed(1)}`,
                percentile ? `Percentile: ${percentile.toFixed(0)}th` : '',
                point.date ? `Date: ${point.date}` : ''
              ].filter(Boolean);
            }
            return `${item.dataset.label}: ${item.parsed.y?.toFixed(1)}`;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Age (years)',
          font: { size: 12, weight: 'bold' },
          color: '#9ca3af'
        },
        grid: { color: 'rgba(255,255,255,0.05)' },
        ticks: { color: '#9ca3af' }
      },
      y: {
        title: {
          display: true,
          text: activeChart === 'height' ? 'Height (cm)' : activeChart === 'weight' ? 'Weight (kg)' : 'BMI (kg/m²)',
          font: { size: 12, weight: 'bold' },
          color: '#9ca3af'
        },
        grid: { color: 'rgba(255,255,255,0.05)' },
        ticks: { color: '#9ca3af' }
      }
    }
  };

  // Latest percentiles
  const latest = childPoints[childPoints.length - 1];
  const latestPercentile = activeChart === 'height' ? latest?.heightPercentile :
                          activeChart === 'weight' ? latest?.weightPercentile : latest?.bmiPercentile;

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#02b5ff]/20 to-[#ff025f]/20 px-6 py-4 border-b border-zinc-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <TrendingUp className="h-5 w-5 text-[#02b5ff]" />
            <div>
              <h3 className="text-lg font-bold text-white">UK-WHO Growth Charts</h3>
              <p className="text-xs text-zinc-400">Clinical-grade centile tracking</p>
            </div>
          </div>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="text-zinc-400 hover:text-white transition-colors"
          >
            {showInfo ? <ChevronUp className="h-5 w-5" /> : <Info className="h-5 w-5" />}
          </button>
        </div>
        
        {showInfo && (
          <div className="mt-4 p-4 bg-zinc-900/50 rounded-lg text-sm text-zinc-400">
            <p className="mb-2">
              <strong className="text-white">About UK-WHO Charts:</strong> These charts use the UK-WHO growth reference, 
              combining WHO standards (0-4 years) and UK 1990 references (4+ years).
            </p>
            <p>
              The <span className="text-[#02b5ff]">cyan line</span> shows your athlete's measurements. 
              The <span className="text-[#ff025f]">pink dashed line</span> shows projected growth based on recent trend.
              Grey lines show population centiles (3rd, 15th, 50th, 85th, 97th).
            </p>
          </div>
        )}
      </div>

      {/* Chart Type Selector */}
      <div className="px-6 py-3 border-b border-zinc-800 flex gap-2">
        <button
          onClick={() => setActiveChart('height')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeChart === 'height'
              ? 'bg-[#02b5ff] text-white'
              : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
          }`}
        >
          <Ruler className="h-4 w-4" />
          Height
        </button>
        <button
          onClick={() => setActiveChart('weight')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeChart === 'weight'
              ? 'bg-[#02b5ff] text-white'
              : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
          }`}
        >
          <Scale className="h-4 w-4" />
          Weight
        </button>
        <button
          onClick={() => setActiveChart('bmi')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeChart === 'bmi'
              ? 'bg-[#02b5ff] text-white'
              : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
          }`}
        >
          <Target className="h-4 w-4" />
          BMI
        </button>
        
        {/* Current Percentile Badge */}
        {latestPercentile && (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-zinc-500">Current:</span>
            <span className={`text-sm font-bold px-3 py-1 rounded-full ${
              latestPercentile < 3 || latestPercentile > 97 
                ? 'bg-[#ff025f]/20 text-[#ff025f]' 
                : latestPercentile < 15 || latestPercentile > 85
                  ? 'bg-yellow-500/20 text-yellow-400'
                  : 'bg-green-500/20 text-green-400'
            }`}>
              {latestPercentile.toFixed(0)}th percentile
            </span>
          </div>
        )}
      </div>

      {/* Chart */}
      <div className="p-6" style={{ height: '400px' }}>
        <Line data={chartData} options={chartOptions} />
      </div>

      {/* Legend */}
      <div className="px-6 py-4 border-t border-zinc-800 bg-zinc-900/30">
        <div className="flex flex-wrap gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-4 h-1 bg-[#02b5ff] rounded"></div>
            <span className="text-zinc-400">{child?.first_name}'s {activeChart}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-1 bg-[#ff025f] rounded" style={{ borderStyle: 'dashed' }}></div>
            <span className="text-zinc-400">Projected trend</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-0.5 bg-green-500/50 rounded"></div>
            <span className="text-zinc-400">50th centile (median)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-0.5 bg-orange-500/50 rounded" style={{ borderStyle: 'dashed' }}></div>
            <span className="text-zinc-400">15th/85th centiles</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-0.5 bg-red-500/50 rounded" style={{ borderStyle: 'dashed' }}></div>
            <span className="text-zinc-400">3rd/97th centiles</span>
          </div>
        </div>
      </div>

      {/* Measurement History */}
      {childPoints.length > 0 && (
        <div className="px-6 py-4 border-t border-zinc-800">
          <h4 className="text-sm font-semibold text-zinc-400 mb-3">Recent Measurements</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {childPoints.slice(-4).reverse().map((point, idx) => (
              <div key={idx} className="bg-zinc-800/50 rounded-lg p-3">
                <div className="text-xs text-zinc-500 mb-1">{point.date}</div>
                <div className="text-sm text-white font-medium">
                  {activeChart === 'height' && `${point.height} cm`}
                  {activeChart === 'weight' && `${point.weight} kg`}
                  {activeChart === 'bmi' && `${point.bmi} BMI`}
                </div>
                <div className="text-xs text-[#02b5ff]">
                  {activeChart === 'height' && point.heightPercentile && `${point.heightPercentile.toFixed(0)}th %ile`}
                  {activeChart === 'weight' && point.weightPercentile && `${point.weightPercentile.toFixed(0)}th %ile`}
                  {activeChart === 'bmi' && point.bmiPercentile && `${point.bmiPercentile.toFixed(0)}th %ile`}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
